// Benachrichtigungen für Chat und Aufgaben.
//
// Fragt im Hintergrund nach — das Backend hat keine Live-Verbindung. Der
// Abstand ist bewusst moderat: eine Glocke, die im Sekundentakt fragt, kostet
// bei vielen Mitarbeitern spürbar Serverlast, ohne dass jemand den Unterschied
// bemerkt.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import Icon from '../../../gemeinsam/komponenten/Icon';
import { useBenachrichtigungen } from '../lib/benachrichtigungen';

export default function Glocke() {
  const { t } = useSprache();
  const navigate = useNavigate();
  // Chat-Zaehler sitzt am Menuepunkt, hier stehen die uebrigen Vorgaenge.
  const { aufgaben, support, anzahlSonstige, alsGesehenMelden } = useBenachrichtigungen();
  const [offen, setOffen] = useState(false);
  const bereich = useRef<HTMLDivElement>(null);

  // Klick außerhalb schließt die Liste.
  useEffect(() => {
    if (!offen) return;
    function beiKlick(e: MouseEvent) {
      if (bereich.current && !bereich.current.contains(e.target as Node)) setOffen(false);
    }
    document.addEventListener('mousedown', beiKlick);
    return () => document.removeEventListener('mousedown', beiKlick);
  }, [offen]);

  async function oeffnen() {
    const neuerZustand = !offen;
    setOffen(neuerZustand);

    if (neuerZustand) {
      // Beim ersten Öffnen nach der Erlaubnis für Systemhinweise fragen — ein
      // Klick des Benutzers ist die einzige Gelegenheit, bei der Browser das
      // zulassen, ohne die Anfrage zu verwerfen.
      if ('Notification' in window && Notification.permission === 'default') {
        void Notification.requestPermission();
      }
      await alsGesehenMelden();
    }
  }

  function springen(ziel: string) {
    setOffen(false);
    navigate(ziel);
  }

  const anzahl = anzahlSonstige;

  return (
    <div className="relative" ref={bereich}>
      <button
        onClick={oeffnen}
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
        aria-label={`${t('benachrichtigung.titel')}${anzahl > 0 ? ` (${anzahl})` : ''}`}
        aria-expanded={offen}
      >
        <Icon name="glocke" />
        {anzahl > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-4.5 min-w-4.5 place-items-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
            {anzahl > 99 ? '99+' : anzahl}
          </span>
        )}
      </button>

      {offen && (
        <div className="absolute end-0 z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('benachrichtigung.titel')}</p>
          </div>

          {aufgaben.length === 0 && support.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('benachrichtigung.leer')}
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              {/* Support zuerst: eine Antwort vom Anbieter wartet auf den Kunden. */}
              {support.length > 0 && (
                <>
                  <p className="bg-slate-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                    {t('nav.support')}
                  </p>
                  {support.map(s => (
                    <button key={s.id} onClick={() => springen('/support')}
                      className="flex w-full items-start gap-2.5 border-b border-slate-100 px-4 py-2.5 text-start hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-marke-600" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-900 dark:text-slate-100">{s.betreff}</span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">{t('support.glocke')} · {s.nummer}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
              {aufgaben.length > 0 && (
                <>
                  <p className="bg-slate-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                    {t('nav.aufgaben')}
                  </p>
                  {aufgaben.map(a => (
                    <button key={a.id} onClick={() => springen('/aufgaben')}
                      className="flex w-full items-start gap-2.5 border-b border-slate-100 px-4 py-2.5 text-start hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/50">
                      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                        a.hinweis === 'überfällig' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-slate-900 dark:text-slate-100">{a.titel}</span>
                        <span className={`block text-xs ${
                          a.hinweis === 'überfällig'
                            ? 'text-rose-600 dark:text-rose-400'
                            : 'text-slate-500 dark:text-slate-400'}`}>{a.hinweis}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
}
